/*
 * User-Menu für die obere rechte Ecke: kompakter Avatar mit Initialen,
 * beim Klick ein Dropdown mit User-Info + Admin-Section (Rollen & Rechte,
 * Typicals) + Sign out. Ersetzt den redundanten User-Block in der
 * Sidebar sowie die globalen Nav-Einträge "Rollen & Rechte" und
 * "Typicals" — administrative Sachen gehören hinter das Konto-Symbol,
 * nicht in die Hauptnavigation.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { sessionSignal, signOut } from '../auth/session';
import { api } from '../api/client';
import './UserMenu.css';

export function UserMenu() {
  const user = sessionSignal.value.user;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Zugewiesene Anlagen (etwa die private Anlage eines Technikers): dann ein
  // Eintrag zur Web-HMI. Einmal beim Oeffnen des Menues gefragt.
  const [myPlants, setMyPlants] = useState<number | null>(null);
  useEffect(() => {
    if (!open || myPlants !== null || !user?.permissions?.includes('webhmi.use')) return;
    api.get<unknown[]>('/api/webhmi/plants').then((p) => setMyPlants(p.length)).catch(() => setMyPlants(0));
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  if (!user) return null;

  const displayName = user.displayName ?? user.email ?? 'Signed in';
  const initials = deriveInitials(displayName);
  const perms = user.permissions ?? [];
  const canAdminIdentity = perms.includes('users.admin') || perms.includes('groups.admin');
  const canEditSchema = perms.includes('plants.schema.edit');
  // Client-Katalog: sichtbar fuer catalog.admin (voll editierbar) und
  // catalog.tenant.admin (nur Tenant-Sperren + Read-View). Beide Rollen
  // bekommen den Menuepunkt hier — die Seite selbst faechert die UI dann
  // je nach Permission auf.
  const canSeeClientCatalog = perms.includes('catalog.admin') || perms.includes('catalog.tenant.admin');
  // Lizenzmodelle: eigenes Recht neben plants.admin. Wer eine Anlage
  // administrieren darf, darf damit nicht auch ihre Obergrenzen anheben.
  const canSeeLicenses = perms.includes('license.admin');
  // Geräte-Freigaben: nur Systemadmins, wartende Geräte gehören noch keinem
  // Datenbereich (docs/entwurf-geraete-selbstanmeldung.md).
  const canApproveDevices = perms.includes('devices.registration.admin');
  const canAdminPortal = perms.includes('portal.admin');
  // Artefakte & OTA sind aus dem UserMenu in die Sidebar gewandert, damit der
  // Zugang mit einem Klick statt zwei erreichbar ist. Der Punkt lebt jetzt in
  // AppShell.tsx.
  const hasAdminSection = canAdminIdentity || canEditSchema || canSeeClientCatalog || canSeeLicenses || canApproveDevices || canAdminPortal;

  return (
    <div class="hv-usermenu" ref={rootRef}>
      <button
        type="button"
        class="hv-usermenu__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Angemeldet als ${displayName}`}
      >
        <span class="hv-usermenu__avatar" aria-hidden="true">{initials}</span>
      </button>
      {open && (
        <div class="hv-usermenu__panel" role="menu">
          <div class="hv-usermenu__head">
            <div class="hv-usermenu__head-name">{displayName}</div>
            {user.email && user.email !== displayName && (
              <div class="hv-usermenu__head-mail">{user.email}</div>
            )}
          </div>
          {hasAdminSection && (
            <>
              <div class="hv-usermenu__divider" />
              <div class="hv-usermenu__section-title">Administration</div>
              {canAdminIdentity && (
                <a href="/settings/identity" class="hv-usermenu__item" role="menuitem"
                   onClick={() => setOpen(false)}>
                  <IconIdentity /> Rollen &amp; Rechte
                </a>
              )}
              {canEditSchema && (
                <a href="/settings/typicals" class="hv-usermenu__item" role="menuitem"
                   onClick={() => setOpen(false)}>
                  <IconTypical /> Typicals
                </a>
              )}
              {canEditSchema && (
                <a href="/settings/recognition" class="hv-usermenu__item" role="menuitem"
                   onClick={() => setOpen(false)}>
                  <IconTypical /> Erkennungssets
                </a>
              )}
              {canSeeClientCatalog && (
                <a href="/settings/client-catalog" class="hv-usermenu__item" role="menuitem"
                   onClick={() => setOpen(false)}>
                  <IconCatalog /> Client-Katalog
                </a>
              )}
              {canSeeLicenses && (
                <a href="/settings/licenses" class="hv-usermenu__item" role="menuitem"
                   onClick={() => setOpen(false)}>
                  <IconLicense /> Lizenzmodelle
                </a>
              )}
              {canApproveDevices && (
                <a href="/settings/device-registrations" class="hv-usermenu__item" role="menuitem"
                   onClick={() => setOpen(false)}>
                  <IconDeviceApproval /> Geräte-Freigaben
                </a>
              )}
              {perms.includes('users.admin') && (
                <a href="/settings/servicezugang" class="hv-usermenu__item" role="menuitem"
                   onClick={() => setOpen(false)}>
                  <IconIdentity /> Servicezugang Panel
                </a>
              )}
              {canAdminPortal && (
                <a href="/settings/portal" class="hv-usermenu__item" role="menuitem"
                   onClick={() => setOpen(false)}>
                  <IconIdentity /> Registrierportal
                </a>
              )}
            </>
          )}
          {(myPlants ?? 0) > 0 && (
            <>
              <div class="hv-usermenu__divider" />
              <a href="/m" class="hv-usermenu__item" role="menuitem" onClick={() => setOpen(false)}>
                <IconTypical /> Meine Anlagen (Web-HMI)
              </a>
            </>
          )}
          <div class="hv-usermenu__divider" />
          <button type="button" class="hv-usermenu__item hv-usermenu__item--danger"
                  role="menuitem" onClick={signOut}>
            <IconSignOut /> Abmelden
          </button>
        </div>
      )}
    </div>
  );
}

function deriveInitials(name: string): string {
  const parts = name.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function IconIdentity() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="10" cy="7" r="3" />
      <path d="M4 17c0-3 3-5 6-5s6 2 6 5" />
    </svg>
  );
}
function IconTypical() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M5 3h7l3 3v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M12 3v3h3" />
      <path d="M7 10h6M7 13h6M7 16h4" />
    </svg>
  );
}
function IconCatalog() {
  // Rack of stacked cards / product tiles — reads as "catalog of hardware".
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="4" width="14" height="4" rx="1" />
      <rect x="3" y="10" width="14" height="4" rx="1" />
      <path d="M6 6h3M6 12h3" />
    </svg>
  );
}
function IconLicense() {
  // Urkunde mit Siegel — liest als "Lizenz", nicht als Dokument allgemein.
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M5 3h10a1 1 0 0 1 1 1v9H4V4a1 1 0 0 1 1-1Z" />
      <path d="M7 6h6M7 9h4" />
      <circle cx="10" cy="15" r="2" />
    </svg>
  );
}
function IconDeviceApproval() {
  // Gerät mit Haken: ein Panel, das auf Freigabe wartet.
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="4" width="11" height="8" rx="1" />
      <path d="M6 15h5" />
      <path d="M12.5 15.5l1.8 1.8 3.2-3.6" />
    </svg>
  );
}
function IconSignOut() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M11 4H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h6" />
      <path d="M14 7l3 3-3 3" />
      <path d="M8 10h9" />
    </svg>
  );
}
